/**
 * AdminLeftRail — widget-local left-rail nav for /admin/*.
 *
 * Pins the floating-capsule treatment from the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` AdminLeftRail):
 * a 56 px sticky vertical pill (`--shadow-md`, `rounded-full`) with
 * 44×44 round slots. Active slot is filled `--bg-brand` with a white
 * (`--fg-on-brand`) glyph; non-active slots are transparent with an
 * `--fg-secondary` glyph and a `--bg-hover` hover. Same skeleton as
 * the PMS dashboard `LeftMenu`, with Admin-specific route items.
 *
 * Four icon-only entries per `NAVIGATION_SPEC.md §3` (Overview /
 * Companies / Staff / Imports). `currentPath` drives the active state;
 * sub-routes activate their parent (e.g. `/admin/companies/[id]`
 * activates Companies, per §6).
 *
 * The artboard renders the rail icon-only (no labels). Labels are an
 * opt-in Density preference (`NAVIGATION_SPEC.md §3`), deferred to the
 * preferences ticket; the `expanded` prop is plumbed for it but this
 * pass renders the icon-only capsule. Each slot keeps an `aria-label`
 * (assistive tech) + native `title` (hover tooltip), so the icon-only
 * rail is fully labelled.
 *
 * Audit log was removed from the original spec; audit now lives
 * per-company inside the company detail page.
 */

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon, type IconName } from '@sparta/ui-kit';

export type AdminLeftRailItemId =
  | 'overview'
  | 'companies'
  | 'staff'
  | 'imports';

interface AdminLeftRailItemDef {
  readonly id: AdminLeftRailItemId;
  readonly label: string;
  readonly icon: IconName;
  readonly route: string;
}

export const ADMIN_LEFT_RAIL_ITEMS: ReadonlyArray<AdminLeftRailItemDef> = [
  { id: 'overview', label: 'Overview', icon: 'grid', route: '/admin' },
  {
    id: 'companies',
    label: 'Companies',
    icon: 'building',
    route: '/admin/companies',
  },
  { id: 'staff', label: 'Staff', icon: 'users', route: '/admin/staff' },
  {
    id: 'imports',
    label: 'Imports',
    icon: 'arrowUp',
    route: '/admin/imports',
  },
];

/**
 * Map a router pathname to the AdminLeftRail item id that should
 * appear "active". Sub-routes activate their parent per
 * `NAVIGATION_SPEC.md §6` ("When the user is on
 * /admin/companies/[id], the Companies item shows active state").
 */
export function pathnameToAdminLeftRailActive(
  pathname: string,
): AdminLeftRailItemId {
  if (pathname.startsWith('/admin/companies')) return 'companies';
  if (pathname.startsWith('/admin/staff')) return 'staff';
  if (pathname.startsWith('/admin/imports')) return 'imports';
  return 'overview';
}

export interface AdminLeftRailProps {
  /**
   * Reserved for the opt-in Density preference (labels beside icons).
   * Not yet wired; the artboard rail is icon-only. Plumbed so the
   * future preferences hook has a mount point.
   */
  readonly expanded?: boolean;
}

export function AdminLeftRail(_props: AdminLeftRailProps = {}): React.ReactElement {
  const location = useLocation();
  const activeId = pathnameToAdminLeftRailActive(location.pathname);

  return (
    // `[box-shadow:var(--shadow-md)]` arbitrary-property form: the
    // `shadow-[var(...)]` shorthand routes to `--tw-shadow-color` and
    // emits no box-shadow. Permitted per ADR-0003 allow-list.
    //
    // `top-[88px]` — SCRUM-79 audit exemption: sticky-nav y-offset
    // (top pill `top-4` + 56 px height + 16 px gap) has no Tailwind
    // default match. Mirrors the dashboard `LeftMenu` exemption.
    <nav
      aria-label="Admin navigation"
      // eslint-disable-next-line @sparta/no-pixel-literal-arbitrary -- SCRUM-79: top-[88px] sticky offset, no Tailwind default; see comment above.
      className="sticky top-[88px] self-start ml-4 shrink-0 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] [box-shadow:var(--shadow-md)] rounded-full p-1.5 flex flex-col gap-0.5 w-14"
    >
      {ADMIN_LEFT_RAIL_ITEMS.map((item) => {
        const isActive = item.id === activeId;
        return (
          <Link
            key={item.id}
            to={item.route}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            title={item.label}
            className={`w-11 h-11 inline-flex items-center justify-center no-underline rounded-full transition-[background] duration-[var(--dur-fast)] ${
              isActive
                ? 'bg-[var(--bg-brand)] text-[var(--fg-on-brand)]'
                : 'bg-transparent text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            <Icon name={item.icon} size={18} />
          </Link>
        );
      })}
    </nav>
  );
}
